import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useStore } from '../store';
import { setBillingCyclesStatusVoid } from '../services/api';
import { getBusinessErrorMessage } from '../utils/apiErrorDetail';
import { billingKeys, BILLING_STALE_TIME_MS } from '../services/queryClient';
import type { BillType } from '../services/api/types.gen';
import type { EoeClaimTabId } from '../components/billing/eoeClaimsManagerTabs';
import type { VoidCopyOptions } from '../components/billing/voidTypes';

type MutateOneFn = (billingCycleId: string) => Promise<unknown>;
type OnAfterReloadFn = () => Promise<void> | void;

export interface BillingSelectedItem {
  id: string;
  name: string;
  mrn?: string;
  /** Kept so an action can check a selected row that is on another page. */
  billType?: BillType;
}

export type MutateSelectedFailure = {
  id: string;
  name: string;
  mrn?: string;
  error: unknown;
};

/** Paginated envelope returned by the billing-cycles endpoints (ROG-2756). */
export interface BillingCyclesEnvelope<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

interface UseBillingCyclesProps<T, P> {
  /**
   * Identifies this tab's cache entry. Required, not derived from the payload:
   * two tabs can send equal payloads to *different* endpoints, and the cache
   * would then hand one tab the other's claims. Today's payloads happen to
   * differ, so this guards the next tab rather than a current bug.
   */
  tabId: EoeClaimTabId;
  fetchFn: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    args: any
  ) => Promise<{ data?: BillingCyclesEnvelope<T>; error?: unknown }>;
  fetchPayload: P;
  errorMessage: string;
  getId: (item: T) => string;
  getName: (item: T) => string;
  getMrn?: (item: T) => string | undefined;
  getBillType?: (item: T) => BillType | undefined;
}

export function useBillingCycles<T, P>({
  tabId,
  fetchFn,
  fetchPayload,
  errorMessage,
  getId,
  getName,
  getMrn,
  getBillType,
}: UseBillingCyclesProps<T, P>) {
  const showError = useStore(state => state.showError);
  const showSuccess = useStore(state => state.showSuccess);
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedItemCache, setSelectedItemCache] = useState<
    Map<string, BillingSelectedItem>
  >(new Map());

  const isApiErrorResponse = (value: unknown): value is { error: unknown } => {
    return (
      typeof value === 'object' &&
      value !== null &&
      'error' in value &&
      (value as { error?: unknown }).error !== undefined
    );
  };

  // Stringify payload to use as a stable dependency for useCallback/useEffect
  const payloadString = JSON.stringify(fetchPayload);

  // Selection persists across pagination (Gmail-like): paging keeps the user's
  // selection so they can batch-act across pages. Any change to the result set
  // itself (search/sort/filter/per_page) clears it, so a stale selection can't
  // outlive the rows it referred to. `page` is the only field stripped here, so
  // a page-only change leaves this key — and the selection — untouched.
  const selectionResetKey = useMemo(() => {
    const parsed = JSON.parse(payloadString) as Record<string, unknown>;
    delete parsed.page;
    return JSON.stringify(parsed);
  }, [payloadString]);

  // The exact cache key this tab is reading. Memoized on the serialized payload
  // so the reference stays stable across renders (the caller recreates its
  // payload object each render); the value is the same array `useQuery` hashes,
  // so a consumer's `exact: true` lookup resolves to this tab's entry.
  const queryKey = useMemo(
    () => billingKeys.billingCycles({ tabId, payload: fetchPayload }),
    [tabId, payloadString]
  );

  // Overlapping list requests (search/sort/filter) used to need a hand-rolled
  // request-id guard so a slow older response could not overwrite newer rows.
  // Query keys replace it: each distinct payload is its own cache entry, so a
  // stale response resolves into the entry nobody is observing.
  const {
    data: envelope,
    isPending,
    isFetching,
    isError,
    isPlaceholderData,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await fetchFn({ body: fetchPayload });
      // The generated client resolves HTTP failures as { error, data: undefined }
      // rather than rejecting, so this has to be checked explicitly. Without it
      // a 500 is cached as a successful empty table and the error toast below
      // can never fire.
      if (response.error) {
        throw response.error;
      }
      // A 2xx with no body is a different case and stays non-fatal: normalize it
      // so consumers never distinguish "no response body" from "zero rows".
      return response.data ?? { items: [], total: 0, total_pages: 0 };
    },
    // Without this, switching page/sort mounts a fresh cache entry with no data
    // and the table blanks mid-request. The previous page stays on screen until
    // the next one lands, which is what the old manual state did implicitly.
    placeholderData: keepPreviousData,
    staleTime: BILLING_STALE_TIME_MS,
    // Toasted once per failed fetch by the shared handler in queryClient.ts,
    // rather than from an effect that would re-fire on every remount.
    meta: {
      errorToast: {
        title: 'Failed to load billing cycles',
        description: errorMessage,
      },
    },
  });

  // A failed load blanks the table rather than leaving the previous page's rows
  // under a new filter, matching the pre-Query behavior.
  const data = useMemo<T[]>(
    () => (isError ? [] : (envelope?.items ?? [])),
    [isError, envelope]
  );
  const total = isError ? 0 : (envelope?.total ?? 0);
  const totalPages = isError ? 0 : (envelope?.total_pages ?? 0);

  // isPending ("nothing to show yet"), NOT isFetching ("a request is in
  // flight"). BillingDataTable renders a single "Loading..." row *instead of*
  // the rows whenever this is true, so isFetching would replace the rows
  // keepPreviousData just retained and reintroduce the blank-table flash on
  // every page/sort change — the two settings would cancel out.
  //
  // First load has no previous page to hold, so isPending is true and the
  // placeholder still shows. Background refetches swap the rows in place.
  const isLoading = isPending;

  // A refetch with rows already on screen — a filter, sort, page or search
  // change. Drives the subtle "Updating..." indicator; deliberately false during
  // the cold load, which the isLoading placeholder already covers.
  const isRefreshing = isFetching && !isPending;

  /**
   * Reload the claims lists after a mutation.
   *
   * Invalidates *every* tab's cache entry, not just this one. A mutation moves a
   * claim between tabs, so refetching only the active tab would leave the others
   * holding rows the change already invalidated — and with a 30s staleTime they
   * would serve those rows straight from cache on the next tab switch. Only the
   * mounted tab refetches now; the rest are marked stale and reload when opened.
   */
  const loadData = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: billingKeys.billingCycles(),
    });
  }, [queryClient]);

  // Reset selection only when the result set changes (not on a page change);
  // see selectionResetKey above.
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectedItemCache(new Map());
  }, [selectionResetKey]);

  useEffect(() => {
    setSelectedItemCache(prev => {
      let changed = false;
      const next = new Map(prev);
      for (const item of data) {
        const id = getId(item);
        if (!selectedIds.has(id)) continue;
        const summary = {
          id,
          name: getName(item),
          mrn: getMrn?.(item),
          billType: getBillType?.(item),
        };
        const existing = next.get(id);
        if (
          existing?.name !== summary.name ||
          existing?.mrn !== summary.mrn ||
          existing?.billType !== summary.billType
        ) {
          next.set(id, summary);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [data, selectedIds, getId, getName, getMrn, getBillType]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectedItemCache(new Map());
  }, []);

  const removeSelectedId = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setSelectedItemCache(prev => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleToggleId = useCallback(
    (id: string) => {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          setSelectedItemCache(prevCache => {
            const nextCache = new Map(prevCache);
            nextCache.delete(id);
            return nextCache;
          });
        } else {
          next.add(id);
          const item = data.find(row => getId(row) === id);
          if (item) {
            setSelectedItemCache(prevCache => {
              const nextCache = new Map(prevCache);
              nextCache.set(id, {
                id,
                name: getName(item),
                mrn: getMrn?.(item),
                billType: getBillType?.(item),
              });
              return nextCache;
            });
          }
        }
        return next;
      });
    },
    [data, getId, getName, getMrn, getBillType]
  );

  const handleToggleAll = useCallback(
    (visibleIds: string[]) => {
      setSelectedIds(prev => {
        const allSelected =
          visibleIds.length > 0 && visibleIds.every(id => prev.has(id));
        const next = new Set(prev);
        if (allSelected) {
          visibleIds.forEach(id => next.delete(id));
          setSelectedItemCache(prevCache => {
            const nextCache = new Map(prevCache);
            visibleIds.forEach(id => nextCache.delete(id));
            return nextCache;
          });
        } else {
          visibleIds.forEach(id => next.add(id));
          setSelectedItemCache(prevCache => {
            const nextCache = new Map(prevCache);
            for (const id of visibleIds) {
              const item = data.find(row => getId(row) === id);
              if (!item) continue;
              nextCache.set(id, {
                id,
                name: getName(item),
                mrn: getMrn?.(item),
                billType: getBillType?.(item),
              });
            }
            return nextCache;
          });
        }
        return next;
      });
    },
    [data, getId, getName, getMrn, getBillType]
  );

  const mutateSelected = useCallback(
    async (
      mutateOne: MutateOneFn,
      failureMessageTitle: string,
      failureMessagePrefix: string,
      onAfterReload?: OnAfterReloadFn,
      onFailure?: (failures: MutateSelectedFailure[]) => void
    ) => {
      setIsSubmitting(true);
      const ids = Array.from(selectedIds);
      try {
        const results = await Promise.allSettled(ids.map(id => mutateOne(id)));
        const failures: MutateSelectedFailure[] = [];

        for (let i = 0; i < ids.length; i += 1) {
          const id = ids[i];
          const item = data.find(e => getId(e) === id);
          const cachedItem = selectedItemCache.get(id);
          const name = item ? getName(item) : (cachedItem?.name ?? id);
          const mrn = item && getMrn ? getMrn(item) : cachedItem?.mrn;
          const result = results[i];

          if (result.status === 'rejected') {
            failures.push({ id, name, mrn, error: result.reason });
            continue;
          }

          if (isApiErrorResponse(result.value)) {
            // Preserve the full AxiosError so callers can read status + detail.
            failures.push({ id, name, mrn, error: result.value });
          }
        }

        if (failures.length > 0) {
          if (onFailure) {
            onFailure(failures);
          } else {
            const failedNames = failures.map(f => f.name).join(', ');
            showError(
              failureMessageTitle,
              `${failureMessagePrefix}: ${failedNames}`
            );
          }
        }
        const hasSuccessfulMutations = failures.length < ids.length;

        clearSelection();
        await loadData();
        if (hasSuccessfulMutations) {
          await onAfterReload?.();
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      selectedIds,
      selectedItemCache,
      data,
      loadData,
      showError,
      getId,
      getName,
      getMrn,
      clearSelection,
    ]
  );

  /**
   * Void every selected cycle in one request. The backend rebuilds adjacent
   * voided ranges as one merged period, which only works when the whole
   * selection travels together, and it is all-or-nothing: on failure nothing
   * was voided, so the selection stays for a retry.
   */
  const voidSelected = useCallback(
    async (options: VoidCopyOptions, onAfterReload?: OnAfterReloadFn) => {
      setIsSubmitting(true);
      const ids = Array.from(selectedIds);
      try {
        // The generated client resolves failures as { error } (never rejects).
        const result = await setBillingCyclesStatusVoid({
          body: {
            billing_cycle_ids: ids,
            copy_worksheet_forward: options.copyWorksheetForward,
            copy_notes_forward: options.copyNotesForward,
          },
        });
        if (result.error) {
          console.error('Failed to void billing cycles:', result);
          showError(
            'Failed to void billing cycles',
            getBusinessErrorMessage(result) ??
              'No billing cycles were voided. Please try again.'
          );
          return;
        }
        const count =
          result.data?.voided_billing_cycle_ids.length ?? ids.length;
        showSuccess(
          count === 1
            ? 'Voided 1 billing cycle'
            : `Voided ${count} billing cycles`
        );
        clearSelection();
        await loadData();
        await onAfterReload?.();
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedIds, loadData, showError, showSuccess, clearSelection]
  );

  const selectedItems = useMemo(
    () =>
      Array.from(
        selectedIds,
        id => selectedItemCache.get(id) ?? { id, name: id }
      ),
    [selectedIds, selectedItemCache]
  );

  return {
    data,
    total,
    totalPages,
    isLoading,
    isRefreshing,
    // The list identity a readiness consumer registers and reads against: the
    // exact key this tab hashes, and whether the rows on screen are a previous
    // list's placeholder. See ClaimReadinessProvider.
    queryKey,
    isPlaceholderData,
    isSubmitting,
    selectedIds,
    selectedItems,
    clearSelection,
    removeSelectedId,
    handleToggleId,
    handleToggleAll,
    mutateSelected,
    voidSelected,
    loadData,
    showError,
  };
}
